import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { FlatList, Keyboard, ListRenderItem, Platform, TextInput, View } from 'react-native'
import { KeyboardAwareFlatList } from 'react-native-keyboard-aware-scroll-view'

import { ObjectFieldTemplateProps } from '@rjsf/core'
import { FlattenSimpleInterpolation } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { getCommonStyles } from '../../styles/commonStyles'
import { Flex1View } from '../../templates/content/CommonViews'

type RJSFObjectProperty = {
  content: React.ReactElement
  name: string
  disabled: boolean
  readonly: boolean
}

const Container = styled(Flex1View)<{
  styles?: { [key: string]: FlattenSimpleInterpolation }
}>`
  align-self: center;
  width: 100%;
  ${({ styles }) => styles?.formBodyPageContainer}
`

// Space kept between the focused input and the iOS keyboard
const IOS_KEYBOARD_GAP = 16

/**
 * This component is a custom template for RJSF ObjectField. It iterates over all top level
 * schema objects and adds them to a scrollable keyboard aware flatlist.
 *
 * Note: Animation is handled at the individual widget level via AnimatedWrapper,
 * not at the form container level. This allows GenAiHeader to remain static
 * while other form widgets animate in/out during triage transitions.
 */

export const BasicObjectFieldTemplate: FunctionComponent<ObjectFieldTemplateProps> = (props) => {
  const {
    properties,
    formContext: {
      loading,
      CustomLoadingIndicator,
      scrollContainerCustomStyles,
      setIsAtTopOfPage,
      insertElementInScrollContainerOnce,
      stickySubmitContainerHeight,
    },
  } = props
  const verticalScroll = useRef<number>(0)
  const [verticalScrollBeforeKeyboard, setVerticalScrollBeforeKeyboard] = useState<number>(0)
  const { colors, fontFamilyMetadata } = useTheme()
  const commonStyles = getCommonStyles(colors, fontFamilyMetadata)
  const listRef = useRef<FlatList<RJSFObjectProperty> | null>(null)

  // KeyboardAwareFlatList looks up the focused input on keyboardWillShow, which on the new architecture fires before
  // the input reports focus, so it adds the bottom inset but never scrolls. By keyboardDidShow the focus is known.
  useEffect(() => {
    if (Platform.OS !== 'ios') return undefined
    const subscription = Keyboard.addListener('keyboardDidShow', ({ endCoordinates }) => {
      const focusedInput = TextInput.State.currentlyFocusedInput()
      // A host component at runtime; typed as possibly the ScrollView class
      const list = listRef.current?.getNativeScrollRef() as View | null | undefined
      if (!focusedInput || !list) return
      // The listener is app-wide. measureLayout fails, skipping the scroll, when the input isn't in this list.
      focusedInput.measureLayout(list, () =>
        focusedInput.measure((_x, _y, _width, height, _pageX, pageY) => {
          const overlap = pageY + height + IOS_KEYBOARD_GAP - endCoordinates.screenY
          if (overlap > 0) {
            listRef.current?.scrollToOffset({ offset: verticalScroll.current + overlap, animated: true })
          }
        }),
      )
    })
    return () => subscription.remove()
  }, [])

  // Android renders edge-to-edge, so the window no longer shrinks for the keyboard and the list stays under it.
  // Ending the list at the keyboard restores adjustResize: Android's ScrollView scrolls the focused input back
  // into view when it shrinks. KeyboardAwareFlatList's Android handling assumes adjustResize, so it stays off.
  const containerRef = useRef<View>(null)
  const [androidKeyboardTop, setAndroidKeyboardTop] = useState<number | null>(null)
  const [androidKeyboardInset, setAndroidKeyboardInset] = useState(0)

  useEffect(() => {
    if (Platform.OS !== 'android') return undefined
    const showSubscription = Keyboard.addListener('keyboardDidShow', ({ endCoordinates }) =>
      setAndroidKeyboardTop(endCoordinates.screenY),
    )
    const hideSubscription = Keyboard.addListener('keyboardDidHide', () => setAndroidKeyboardTop(null))
    return () => {
      showSubscription.remove()
      hideSubscription.remove()
    }
  }, [])

  // Measured rather than derived from the keyboard height, since where the form ends relative to the navigation bar
  // depends on the screen. Re-measured on layout because the submit button hides once the keyboard opens.
  // pageY shares the keyboard's coordinate space; measureInWindow's y is offset by the status bar on Android.
  const updateAndroidKeyboardInset = useCallback(() => {
    if (androidKeyboardTop === null) {
      setAndroidKeyboardInset(0)
      return
    }
    containerRef.current?.measure((_x, _y, _width, height, _pageX, pageY) => {
      setAndroidKeyboardInset(Math.max(0, pageY + height - androidKeyboardTop))
    })
  }, [androidKeyboardTop])

  useEffect(() => {
    updateAndroidKeyboardInset()
  }, [updateAndroidKeyboardInset])

  const listItem: ListRenderItem<RJSFObjectProperty> = ({ item }) => {
    return item.content
  }

  return (
    <View
      ref={containerRef}
      onLayout={updateAndroidKeyboardInset}
      style={{ flexGrow: 1, paddingBottom: androidKeyboardInset }}
    >
      <Container styles={scrollContainerCustomStyles}>
        {insertElementInScrollContainerOnce}
        {!loading ? (
          <KeyboardAwareFlatList
            keyboardOpeningTime={Number.MAX_SAFE_INTEGER}
            innerRef={(ref: any) => {
              listRef.current = ref
            }}
            enableResetScrollToCoords
            resetScrollToCoords={Platform.OS === 'ios' ? { x: 0, y: verticalScrollBeforeKeyboard } : undefined}
            onKeyboardWillShow={() => {
              setVerticalScrollBeforeKeyboard(verticalScroll.current)
            }}
            style={[commonStyles.flex1View, scrollContainerCustomStyles?.scrollContainerContentCustomStyles]}
            contentContainerStyle={
              stickySubmitContainerHeight ? { paddingBottom: stickySubmitContainerHeight } : undefined
            }
            renderItem={listItem}
            keyExtractor={(item) => item?.content?.key}
            data={properties}
            extraScrollHeight={50}
            showsVerticalScrollIndicator={false}
            onScroll={(event) => {
              verticalScroll.current = event.nativeEvent.contentOffset.y
              if (setIsAtTopOfPage) {
                setIsAtTopOfPage(verticalScroll.current < 1)
              }
            }}
          />
        ) : !!CustomLoadingIndicator ? (
          <CustomLoadingIndicator />
        ) : (
          <LoadingIndicator />
        )}
      </Container>
    </View>
  )
}
